import { useEffect, useMemo, useState } from 'react';
import { Icon } from '../components/Icon';
import { api, ApiError } from '../lib/api';
import { parseDateOnly } from '../data/sampleData';
import { FaAmendments } from './FaAmendments';
import { NonClients } from './NonClients';

// The Exception ledger page: every kind of exception in one place (Bettina,
// 2026-10-21) — SPCs and IRs ingested from Zoho Sign, and franchise agreement
// amendments entered here — plus the Non-clients list (2026-10-29).
export function ExceptionLedger({ role }: { role: 'admin' | 'reviewer' }) {
  const [tab, setTab] = useState<'zoho' | 'fa' | 'nonclients'>('zoho');
  const tabBtn = (id: 'zoho' | 'fa' | 'nonclients', label: string) => (
    <button className="btn btn-sm" onClick={() => setTab(id)}
            style={{ fontWeight: tab === id ? 600 : 400, background: tab === id ? 'var(--accent-soft)' : undefined, borderColor: tab === id ? 'var(--accent)' : undefined }}>
      {label}
    </button>
  );
  return (
    <div className="scroll-y" style={{ flex: 1, padding: 24 }}>
      <div style={{ fontSize: 11.5, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>Reference</div>
      <h1 style={{ margin: '4px 0 12px', fontSize: 22, fontWeight: 600 }}>Exception ledger</h1>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {tabBtn('zoho', 'SPCs & IRs (Zoho Sign)')}
        {tabBtn('fa', 'FA amendments')}
        {tabBtn('nonclients', 'Non-clients')}
      </div>
      {tab === 'zoho' ? <ZohoLedger /> : tab === 'fa' ? <FaAmendments role={role} /> : <NonClients role={role} />}
    </div>
  );
}

interface LedgerEntry {
  id: number;
  type: 'spc' | 'internal_referral';
  granting_af: string;
  granting_state?: string | null;    // state of each side's territory (2026-10-22)
  receiving_state?: string | null;
  receiving_af: string;
  scope_kind: 'client' | 'payer' | 'referral_source' | 'entire_territory' | 'zip';
  scope_value: string | null;
  valid_from: string | null;
  valid_to: string | null;
  both_signed: boolean;
  source_artifact_id: string | null;
  last_synced_at: string | null;
  currently_valid: boolean;
}

interface Summary {
  by_type: Record<string, number>;
  by_scope: { scope: string; n: number }[];
  active_status: { active: number; expired: number; unsigned: number };
}

interface LedgerDetail extends LedgerEntry {
  raw: {
    request_name?: string;
    template_ids?: string[];
    fields?: Record<string, string>;
  } | null;
}

const SCOPE_LABEL: Record<string, string> = {
  client: 'Client',
  payer: 'Payer',
  referral_source: 'Referral source',
  entire_territory: 'Entire territory',
  zip: 'ZIP',
};

function ZohoLedger() {
  const [type, setType] = useState<'all' | 'spc' | 'internal_referral'>('all');
  const [status, setStatus] = useState<'all' | 'active' | 'expired' | 'unsigned'>('active');
  const [scopeKind, setScopeKind] = useState<string>('all');
  const [q, setQ] = useState('');
  const [af, setAf] = useState('');
  const [state, setState] = useState('all');
  const [states, setStates] = useState<string[]>([]);
  const [entries, setEntries] = useState<LedgerEntry[] | null>(null);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [openId, setOpenId] = useState<number | null>(null);

  const queryString = useMemo(() => {
    const p = new URLSearchParams();
    if (type !== 'all') p.set('type', type);
    if (status !== 'all') p.set('status', status);
    if (scopeKind !== 'all') p.set('scope_kind', scopeKind);
    if (q.trim()) p.set('q', q.trim());
    if (af.trim()) p.set('af', af.trim());
    if (state !== 'all') p.set('state', state);
    const s = p.toString();
    return s ? `?${s}` : '';
  }, [type, status, scopeKind, q, af, state]);

  const load = () => {
    api<{ entries: LedgerEntry[]; states?: string[] }>(`/api/exception-ledger${queryString}`)
      .then((d) => { setEntries(d.entries); if (d.states) setStates(d.states); })
      .catch((e) => setError(e instanceof Error ? e.message : 'load failed'));
    api<Summary>('/api/exception-ledger/summary').then(setSummary).catch(() => {});
  };
  useEffect(load, [queryString]);

  const syncNow = async () => {
    setSyncing(true);
    setError(null);
    try {
      await api('/api/admin/sync/spc', { method: 'POST' });
      load();
    } catch (e) {
      const msg = e instanceof ApiError && e.status === 403
        ? 'Admin role required to sync.'
        : (e instanceof Error ? e.message : 'sync failed');
      setError(msg);
    } finally { setSyncing(false); }
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>SPCs &amp; Internal Referrals</h2>
          <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4 }}>
            Service Permission Contracts (SPCs) and Internal Referrals (IRs) ingested from Zoho Sign.
            The audit engine matches these against cases at evaluation time; reviewers can apply one
            from the case detail to clear an open case.
          </div>
        </div>
        <button className="btn" onClick={syncNow} disabled={syncing}>
          <Icon name="refresh" size={13} /> {syncing ? 'Syncing…' : 'Sync from Zoho'}
        </button>
      </div>

      {summary && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 16 }}>
          <SummaryStat label="SPCs" value={summary.by_type['spc'] ?? 0} />
          <SummaryStat label="Internal referrals" value={summary.by_type['internal_referral'] ?? 0} />
          <SummaryStat label="Active" value={summary.active_status.active} sub="signed and not expired" tone="ok" />
          <SummaryStat label="Expired" value={summary.active_status.expired} sub="past valid_to" tone="warn" />
        </div>
      )}

      <div className="card card-pad" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <FilterField label="Type">
            <select className="select" value={type} onChange={(e) => setType(e.target.value as typeof type)}>
              <option value="all">All</option>
              <option value="spc">SPC</option>
              <option value="internal_referral">Internal referral</option>
            </select>
          </FilterField>
          <FilterField label="Status">
            <select className="select" value={status} onChange={(e) => setStatus(e.target.value as typeof status)}>
              <option value="all">All</option>
              <option value="active">Active</option>
              <option value="expired">Expired</option>
              <option value="unsigned">Unsigned</option>
            </select>
          </FilterField>
          <FilterField label="Scope kind">
            <select className="select" value={scopeKind} onChange={(e) => setScopeKind(e.target.value)}>
              <option value="all">All</option>
              <option value="client">Client</option>
              <option value="payer">Payer</option>
              <option value="referral_source">Referral source</option>
              <option value="entire_territory">Entire territory</option>
              <option value="zip">ZIP</option>
            </select>
          </FilterField>
          <FilterField label="State (either territory)">
            <select className="select" value={state} onChange={(e) => setState(e.target.value)}>
              <option value="all">All states</option>
              {states.map((st) => <option key={st} value={st}>{st}</option>)}
            </select>
          </FilterField>
          <FilterField label="AF# (granting or receiving)">
            <input type="text" className="input" style={{ width: 160 }} placeholder="AF-126" value={af} onChange={(e) => setAf(e.target.value)} />
          </FilterField>
          <FilterField label="Search scope value">
            <input type="text" className="input" style={{ width: 200 }} placeholder="client name, payer, zip, …" value={q} onChange={(e) => setQ(e.target.value)} />
          </FilterField>
          <div style={{ flex: 1 }} />
          <span className="muted" style={{ fontSize: 12.5 }}>{entries?.length ?? '…'} {entries?.length === 1 ? 'entry' : 'entries'}</span>
        </div>
      </div>

      {error && <div className="card card-pad c-warn" style={{ fontSize: 13, marginBottom: 12 }}>{error}</div>}
      {!entries && !error && <div className="card card-pad muted" style={{ fontSize: 13 }}>Loading…</div>}
      {entries && entries.length === 0 && <div className="card card-pad muted" style={{ fontSize: 13 }}>No ledger entries match the current filters.</div>}
      {openId != null && <DetailModal id={openId} onClose={() => setOpenId(null)} />}

      {entries && entries.length > 0 && (
        <div className="card">
          <table className="tbl">
            <thead>
              <tr>
                <th>Type</th>
                <th>Granting → Receiving</th>
                <th>State</th>
                <th>Scope</th>
                <th>Valid from</th>
                <th>Valid through</th>
                <th>Status</th>
                <th>Source</th>
                <th>Last sync</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => (
                <tr key={e.id} className="clickable" onClick={() => setOpenId(e.id)}>
                  <td className="mono" style={{ fontSize: 11.5 }}>{e.type === 'spc' ? 'SPC' : 'IR'}</td>
                  <td className="mono" style={{ fontSize: 12 }}>{e.granting_af} → {e.receiving_af}</td>
                  <td className="mono" style={{ fontSize: 12 }}
                      title={`${e.granting_af}: ${e.granting_state ?? 'unknown'} · ${e.receiving_af}: ${e.receiving_state ?? 'unknown'}`}>
                    {e.granting_state && e.receiving_state && e.granting_state !== e.receiving_state
                      ? `${e.granting_state} → ${e.receiving_state}`
                      : (e.granting_state ?? e.receiving_state ?? '—')}
                  </td>
                  <td>
                    <div>{SCOPE_LABEL[e.scope_kind] ?? e.scope_kind}</div>
                    {e.scope_value && <div className="muted" style={{ fontSize: 11.5 }}>{e.scope_value}</div>}
                  </td>
                  <td className="mono" style={{ fontSize: 11.5 }}>{e.valid_from ? parseDateOnly(e.valid_from)!.toLocaleDateString() : '—'}</td>
                  <td className="mono" style={{ fontSize: 11.5 }}>{e.valid_to ? parseDateOnly(e.valid_to)!.toLocaleDateString() : '—'}</td>
                  <td>
                    {e.currently_valid
                      ? <span className="c-ok" style={{ fontSize: 12 }}><span className="pill-dot dot-ok"></span> Active</span>
                      : !e.both_signed
                        ? <span className="muted" style={{ fontSize: 12 }}><span className="pill-dot dot-info"></span> Unsigned</span>
                        : <span className="c-warn" style={{ fontSize: 12 }}><span className="pill-dot dot-warn"></span> Expired</span>}
                  </td>
                  <td className="mono muted" style={{ fontSize: 11 }}>{e.source_artifact_id ?? '—'}</td>
                  <td className="muted mono" style={{ fontSize: 11 }}>{e.last_synced_at ? new Date(e.last_synced_at).toLocaleDateString() : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function DetailModal({ id, onClose }: { id: number; onClose: () => void }) {
  const [entry, setEntry] = useState<LedgerDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    api<{ entry: LedgerDetail }>(`/api/exception-ledger/${id}`)
      .then((d) => setEntry(d.entry))
      .catch((e) => setError(e instanceof Error ? e.message : 'load failed'));
  }, [id]);

  const downloadAgreement = async () => {
    setDownloading(true);
    setError(null);
    try {
      const res = await fetch(`/api/exception-ledger/${id}/document`, { credentials: 'include' });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.error ?? `Download failed (${res.status})`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${entry?.granting_af}-${entry?.receiving_af}-${entry?.source_artifact_id}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'download failed');
    } finally { setDownloading(false); }
  };

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 200,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: 'var(--surface)', borderRadius: 8, padding: 18, width: 720, maxHeight: '85vh', overflow: 'auto',
        border: '1px solid var(--border)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
          <div>
            <div className="muted" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>
              {entry?.type === 'spc' ? 'Service Permission Contract' : entry?.type === 'internal_referral' ? 'Internal Referral' : 'Ledger entry'}
            </div>
            {entry && (
              <h3 style={{ margin: '4px 0 0', fontSize: 16, fontWeight: 600 }}>
                <span className="mono">{entry.granting_af}</span> → <span className="mono">{entry.receiving_af}</span>
              </h3>
            )}
          </div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}><Icon name="close" size={12} /></button>
        </div>

        {!entry && !error && <div className="muted" style={{ fontSize: 12.5 }}>Loading…</div>}
        {error && <div className="c-warn" style={{ fontSize: 12.5, marginBottom: 12 }}>{error}</div>}

        {entry && (
          <>
            <Section title="Parsed by audit engine">
              <Row k="Type" val={entry.type === 'spc' ? 'SPC' : 'IR'} />
              <Row k="Granting AF" val={<span className="mono">{entry.granting_af}</span>} />
              <Row k="Receiving AF" val={<span className="mono">{entry.receiving_af}</span>} />
              <Row k="Scope" val={`${SCOPE_LABEL[entry.scope_kind] ?? entry.scope_kind}${entry.scope_value ? ' · ' + entry.scope_value : ''}`} />
              <Row k="Valid from" val={entry.valid_from ? parseDateOnly(entry.valid_from)!.toLocaleDateString() : '—'} />
              <Row k="Valid through" val={entry.valid_to ? parseDateOnly(entry.valid_to)!.toLocaleDateString() : 'no expiry'} />
              <Row k="Both signed" val={entry.both_signed ? 'Yes' : 'No'} />
              <Row k="Currently valid" val={entry.currently_valid
                ? <span className="c-ok">Active</span>
                : entry.both_signed
                  ? <span className="c-warn">Expired</span>
                  : <span className="muted">Unsigned</span>} />
            </Section>

            {entry.raw && (
              <Section title="Source Zoho Sign envelope">
                <Row k="Envelope name" val={entry.raw.request_name ?? '—'} />
                <Row k="Envelope ID" val={<span className="mono">{entry.source_artifact_id}</span>} />
                {entry.raw.template_ids && entry.raw.template_ids.length > 0 && (
                  <Row k="Template ID(s)" val={<span className="mono">{entry.raw.template_ids.join(', ')}</span>} />
                )}
                <Row k="Last synced from Zoho" val={entry.last_synced_at ? new Date(entry.last_synced_at).toLocaleString() : '—'} />
              </Section>
            )}

            {entry.raw?.fields && (
              <Section title="Raw envelope fields (verification)">
                <div style={{ fontSize: 11.5, fontFamily: 'var(--font-mono)', background: 'var(--surface-2)', padding: 10, borderRadius: 4, maxHeight: 220, overflow: 'auto' }}>
                  {Object.entries(entry.raw.fields)
                    .filter(([_, v]) => v != null && v !== '')
                    .map(([k, v]) => (
                      <div key={k} style={{ display: 'grid', gridTemplateColumns: '180px 1fr', gap: 8, padding: '2px 0' }}>
                        <span className="muted">{k}</span>
                        <span>{String(v)}</span>
                      </div>
                    ))}
                </div>
              </Section>
            )}

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--border)' }}>
              <button className="btn btn-sm" disabled={downloading} onClick={downloadAgreement}>
                <Icon name="download" size={12} /> {downloading ? 'Downloading…' : 'Download signed PDF'}
              </button>
              <a
                className="btn btn-sm"
                href={`https://sign.zoho.com/zs/u#/requests?fid=${entry.source_artifact_id}`}
                target="_blank" rel="noreferrer"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, textDecoration: 'none' }}
              >
                <Icon name="ext" size={12} /> Open in Zoho Sign
              </a>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div className="muted" style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>{title}</div>
      {children}
    </div>
  );
}

function Row({ k, val }: { k: string; val: React.ReactNode }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: 12, padding: '4px 0', borderBottom: '1px solid var(--border)', fontSize: 12.5 }}>
      <div className="muted">{k}</div>
      <div>{val}</div>
    </div>
  );
}

function FilterField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <label style={{ fontSize: 11.5, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600 }}>{label}</label>
      {children}
    </div>
  );
}

function SummaryStat({ label, value, sub, tone }: { label: string; value: number; sub?: string; tone?: 'ok' | 'warn' }) {
  const valueClass = tone === 'ok' ? 'c-ok' : tone === 'warn' ? 'c-warn' : '';
  return (
    <div className="card card-pad">
      <div className="kpi-label" style={{ fontSize: 11.5, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>{label}</div>
      <div className={`num ${valueClass}`} style={{ fontSize: 26, fontWeight: 600, marginTop: 4 }}>{value}</div>
      {sub && <div className="muted" style={{ fontSize: 11.5, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}
